/**
 * Создание ТЗ.
 *
 * Смысл шаблонов — чтобы типовая задача создавалась почти без ввода:
 * выбрал «КАРТОЧКИ», указал количество, нажал «Создать». Цены, монтажер
 * и сроки подставились сами. При 150 видео в месяц это экономит часы.
 *
 * Справа — сводка того, что произойдёт после нажатия. Человек должен
 * понимать последствия до, а не узнавать их после.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { Page, PageHeader } from '../components/Layout';
import { Field, Row, Toggle, toast } from '../components/ui';
import { money } from '../lib/format';
import type { Task, TeamMember, Template } from '../lib/types';

export function CreateTaskPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data: templates } = useQuery({
    queryKey: ['templates'],
    queryFn: () => api.get<Template[]>('/api/templates'),
  });
  const { data: team } = useQuery({
    queryKey: ['team'],
    queryFn: () => api.get<TeamMember[]>('/api/users'),
  });

  const [templateId, setTemplateId] = useState('');
  const [title, setTitle] = useState('');
  const [comment, setComment] = useState('');
  const [quantity, setQuantity] = useState(10);
  const [priceShoot, setPriceShoot] = useState(500);
  const [priceEdit, setPriceEdit] = useState(150);
  const [workerId, setWorkerId] = useState('');
  const [editorId, setEditorId] = useState('');
  const [shootHours, setShootHours] = useState(24);
  const [editSlaHours, setEditSlaHours] = useState(24);
  const [mode, setMode] = useState<'WITH_EDITOR' | 'SOLO'>('WITH_EDITOR');
  const [repeat, setRepeat] = useState(false);
  const [repeatDays, setRepeatDays] = useState(7);
  const [keepFiles, setKeepFiles] = useState(false);
  const [requireCover, setRequireCover] = useState(false);

  // Выбор шаблона подставляет все его дефолты
  useEffect(() => {
    const t = templates?.find((x) => x.id === templateId);
    if (!t) return;
    // Название начинается с имени шаблона — дальше дописываешь своё.
    // Затирать уже введённое нельзя: человек мог набрать текст,
    // а потом передумать с шаблоном
    setTitle((prev) => {
      const wasTemplateName = templates?.some((x) => x.name === prev);
      return !prev || wasTemplateName ? t.name : prev;
    });
    setPriceShoot(t.defaultPriceShoot / 100);
    setPriceEdit(t.defaultPriceEdit / 100);
    setComment(t.defaultComment ?? '');
    setShootHours(t.defaultShootHours);
    setEditSlaHours(t.defaultEditHours);
    setMode(t.defaultMode);
    setRequireCover(t.defaultRequireCover);
    if (t.defaultEditorId) setEditorId(t.defaultEditorId);
  }, [templateId, templates]);

  useEffect(() => {
    if (!templateId && templates?.length) setTemplateId(templates[0]!.id);
  }, [templates, templateId]);

  const workers = team?.filter((u) => u.roles.includes('worker') && u.status === 'ACTIVE') ?? [];
  const editors = team?.filter((u) => u.roles.includes('editor') && u.status === 'ACTIVE') ?? [];

  useEffect(() => {
    if (!workerId && workers.length) setWorkerId(workers[0]!.id);
  }, [workers, workerId]);

  const create = useMutation({
    mutationFn: () =>
      api.post<Task>('/api/tasks', {
        templateId: templateId || undefined,
        title: title.trim() || undefined,
        comment: comment || undefined,
        quantity,
        priceShoot,
        priceEdit,
        mode,
        workerId,
        editorId: mode === 'SOLO' ? null : editorId || null,
        shootHours,
        editSlaHours,
        repeatEveryDays: repeat ? repeatDays : null,
        keepFiles,
        requireCover,
      }),
    onSuccess: (task) => {
      void qc.invalidateQueries({ queryKey: ['tasks'] });
      toast(`Создано ТЗ · ${quantity} видео`);
      navigate(`/tasks/${task.id}`);
    },
    onError: (e: Error) => toast(e.message, '⚠'),
  });

  const worker = team?.find((u) => u.id === workerId);
  const total = (priceShoot + priceEdit) * quantity;

  return (
    <>
      <PageHeader title="Создание ТЗ" back="/tasks" />
      <Page>
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-5 items-start">
          <div className="card p-6">
            <Field
              label="Шаблон"
              hint="✦ Шаблон подставил цены, монтажера и сроки — любое поле можно изменить"
            >
              <select
                className="input"
                value={templateId}
                onChange={(e) => setTemplateId(e.target.value)}
              >
                {templates?.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </Field>

            <Field
              label="Название ТЗ"
              hint="✦ Подставилось из шаблона — допиши, чем это ТЗ отличается от остальных"
            >
              <input
                className="input"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                maxLength={120}
                placeholder="Обзор техники"
              />
            </Field>

            <Field label="Комментарий к ТЗ">
              <textarea
                className="input min-h-[84px] resize-y leading-relaxed"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Что снимать, какие моды, требования к съёмке..."
              />
            </Field>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Field label="Цена съёмки, ₽">
                <input
                  className="input mono"
                  type="number"
                  min={0}
                  value={priceShoot}
                  onChange={(e) => setPriceShoot(Number(e.target.value))}
                />
              </Field>
              <Field label="Цена монтажа, ₽">
                <input
                  className="input mono"
                  type="number"
                  min={0}
                  value={priceEdit}
                  onChange={(e) => setPriceEdit(Number(e.target.value))}
                />
              </Field>
              <Field label="Количество видео">
                <input
                  className="input mono"
                  type="number"
                  min={1}
                  max={100}
                  value={quantity}
                  onChange={(e) => setQuantity(Number(e.target.value))}
                />
              </Field>
            </div>

            <Field label="Кто делает">
              <div className="flex gap-2">
                {(
                  [
                    ['WITH_EDITOR', 'Съёмщик и монтажер'],
                    ['SOLO', 'Один человек делает всё'],
                  ] as const
                ).map(([value, label]) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setMode(value)}
                    className={`btn flex-1 ${mode === value ? 'btn-primary' : ''}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </Field>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Исполнитель съёмки">
                <select
                  className="input"
                  value={workerId}
                  onChange={(e) => setWorkerId(e.target.value)}
                >
                  {workers.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.displayName}
                      {u.activeVideos ? ` · в работе ${u.activeVideos}` : ''}
                    </option>
                  ))}
                </select>
              </Field>

              {mode === 'WITH_EDITOR' && (
                <Field label="Монтажер">
                  <select
                    className="input"
                    value={editorId}
                    onChange={(e) => setEditorId(e.target.value)}
                  >
                    <option value="">Автоматически при загрузке</option>
                    {editors.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.displayName}
                      </option>
                    ))}
                  </select>
                </Field>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Срок съёмки, часов">
                <input
                  className="input mono"
                  type="number"
                  min={1}
                  value={shootHours}
                  onChange={(e) => setShootHours(Number(e.target.value))}
                />
              </Field>
              {mode === 'WITH_EDITOR' && (
                <Field
                  label="Срок монтажа, часов"
                  hint="Отсчёт начнётся с момента загрузки материала"
                >
                  <input
                    className="input mono"
                    type="number"
                    min={1}
                    value={editSlaHours}
                    onChange={(e) => setEditSlaHours(Number(e.target.value))}
                  />
                </Field>
              )}
            </div>

            <div className="flex justify-between items-center py-2">
              <div>
                <div className="font-semibold">Повторять автоматически</div>
                <div className="text-[11.5px] text-muted2 mt-0.5">
                  Создавать такое же ТЗ каждые N дней
                </div>
              </div>
              <div className="flex items-center gap-3">
                {repeat && (
                  <input
                    className="input mono w-20 text-center"
                    type="number"
                    min={1}
                    value={repeatDays}
                    onChange={(e) => setRepeatDays(Number(e.target.value))}
                  />
                )}
                <Toggle on={repeat} onChange={setRepeat} />
              </div>
            </div>

            <div className="flex justify-between items-center py-2 border-t border-[#1A1A24]">
              <div>
                <div className="font-semibold">Обложка обязательна</div>
                <div className="text-[11.5px] text-muted2 mt-0.5">
                  Без загруженной обложки видео нельзя будет принять
                </div>
              </div>
              <Toggle on={requireCover} onChange={setRequireCover} />
            </div>

            <div className="flex justify-between items-center py-2 border-t border-[#1A1A24]">
              <div>
                <div className="font-semibold">Не удалять файлы</div>
                <div className="text-[11.5px] text-muted2 mt-0.5">
                  Отключить автоочистку для этого ТЗ
                </div>
              </div>
              <Toggle on={keepFiles} onChange={setKeepFiles} />
            </div>

            <button
              className="btn btn-primary w-full py-3 mt-4"
              onClick={() => create.mutate()}
              disabled={create.isPending || !workerId}
            >
              {create.isPending ? 'Создаю…' : 'Создать ТЗ'}
            </button>
          </div>

          <div>
            <div className="sec">Что произойдёт</div>
            <div className="card text-[13px] space-y-3">
              <Step text={`Создастся ${quantity} отдельных видео`} sub="каждое со своим статусом и оплатой" first />
              <Step
                text={`${worker?.displayName ?? 'Исполнитель'} получит уведомление`}
                sub="в Telegram, сразу после создания"
              />
              {mode === 'WITH_EDITOR' && (
                <Step
                  text={editorId ? 'Монтажер уже назначен' : 'Монтажер назначится сам'}
                  sub={
                    editorId
                      ? 'получит материал, когда его загрузят'
                      : 'по загрузке, когда придёт материал'
                  }
                />
              )}
              <Step
                text="Деньги начислятся при приёмке"
                sub={`${money(priceShoot * 100)} за съёмку, ${money(priceEdit * 100)} за монтаж — за каждое видео`}
              />
            </div>

            <div className="sec">Итого по ТЗ</div>
            <div className="card">
              <Row label={`Съёмка · ${quantity} × ${priceShoot} ₽`}>
                <span className="mono">{money(priceShoot * quantity * 100)}</span>
              </Row>
              <Row label={`Монтаж · ${quantity} × ${priceEdit} ₽`}>
                <span className="mono">{money(priceEdit * quantity * 100)}</span>
              </Row>
              <Row label={<b className="text-ink">Всего</b>}>
                <span className="mono text-[17px]">{money(total * 100)}</span>
              </Row>
            </div>
          </div>
        </div>
      </Page>
    </>
  );
}

function Step({ text, sub, first }: { text: string; sub: string; first?: boolean }) {
  return (
    <div className="flex gap-3">
      <div
        className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${first ? 'bg-accent' : 'bg-[#31314A]'}`}
      />
      <div>
        <div>{text}</div>
        <div className="text-[11.5px] text-muted2 mt-0.5">{sub}</div>
      </div>
    </div>
  );
}
